'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { geoAlbersUsa, geoMercator, geoPath } from 'd3-geo';
import type { GeoPermissibleObjects } from 'd3-geo';
import { feature } from 'topojson-client';
import { scaleLinear, scaleLog, scaleQuantile } from 'd3-scale';
import type { Topology, GeometryCollection } from 'topojson-specification';
import { cn, warnOnce } from '@/hen-ui/utils';
import {
  SEQUENTIAL_PALETTE_BLUES,
  DIVERGING_PALETTE_BLUE_ORANGE,
  getHexFallback,
} from '../chart-palette';
// Vendored at build time from us-atlas — never fetched at runtime.
import usStatesTopo from './data/us-states.topo.json';
import usCountiesTopo from './data/us-counties.topo.json';

const NO_DATA_COLOR = '#e2e8f0';
// Sourced from the shared sequential/diverging palette families so the
// choropleth gradient and the series-chart color scales share one vocabulary.
// d3-scale cannot parse CSS `var(...)`, so the hex fallbacks are extracted.
const SEQUENTIAL_DEFAULT_RANGE: [string, string] = [
  getHexFallback(SEQUENTIAL_PALETTE_BLUES[0]),
  getHexFallback(SEQUENTIAL_PALETTE_BLUES[SEQUENTIAL_PALETTE_BLUES.length - 1]),
];
const DIVERGING_DEFAULT_COLORS: [string, string, string] = [
  getHexFallback(DIVERGING_PALETTE_BLUE_ORANGE[0]), // low / loss arm
  getHexFallback(DIVERGING_PALETTE_BLUE_ORANGE[1]), // neutral midpoint
  getHexFallback(DIVERGING_PALETTE_BLUE_ORANGE[2]), // high / gain arm
];

export interface HeatmapChartDatum {
  /** FIPS code string — 2-digit for `us-states`, 5-digit for `counties` */
  geoId: string;
  /** Numeric value to map to color; `null` renders the no-data fill */
  value: number | null;
}

export interface HeatmapChartProps {
  /**
   * One row per geographic region. `geoId` is a FIPS code string:
   *  - `us-states` level: 2-digit state FIPS (e.g. `"37"` for NC)
   *  - `counties` level: 5-digit county FIPS (e.g. `"37119"` for Mecklenburg)
   * `value: null` → rendered in no-data color; tooltip shows "No data".
   */
  data: HeatmapChartDatum[];
  /**
   * Geographic resolution to render.
   * - `'us-states'`: all 50 US states
   * - `'counties'`: counties for the state specified by `stateFips`
   */
  level: 'us-states' | 'counties';
  /**
   * 2-digit state FIPS code to filter counties when `level` is `'counties'`.
   * Required when `level='counties'` — omitting it renders an error state.
   * Ignored when `level='us-states'`.
   *
   * @example stateFips="37" // North Carolina
   * @example stateFips="48" // Texas
   * @example stateFips="06" // California
   */
  stateFips?: string;
  /**
   * Override the auto-fitted projection config (center + scale).
   *
   * **Try `scaleFactor` first** — it multiplies the auto-computed scale
   * uniformly and covers the vast majority of zoom-in / zoom-out adjustments
   * without requiring manual coordinate knowledge.
   *
   * Use `projectionConfig` only when the auto-fit result is noticeably
   * off-center or wrongly-scaled (rare).
   *
   * **Note:** `center` only applies to `level="counties"` (Mercator projection).
   * `geoAlbersUsa` (`level="us-states"`) is a composite projection that does
   * not support a center override — only `scale` is applied there.
   *
   * @example projectionConfig={{ center: [-99.5, 31.5], scale: 2400 }}
   */
  projectionConfig?: { center?: [number, number]; scale?: number };
  /**
   * Color normalization mode (default `'linear'`).
   * - `'log'`: Uses `log1p` mapping for values ≤ 0 (shifts domain to [log1p(min), log1p(max)]).
   *   This prevents domain errors from zero/negative values at the cost of scale compressing
   *   small differences at the high end. Document in PR if data is always positive.
   */
  normalization?: 'linear' | 'log' | 'quantile';
  /** Color scale type (default `'sequential'`) */
  colorScale?: 'sequential' | 'diverging';
  /** Override domain/range; defaults are derived from data */
  colorScaleConfig?: {
    domain?: [number, number];
    range?: [string, string];
  };
  /** Custom tooltip text; defaults to `${name}: ${value ?? 'No data'}` where `name` is the resolved region name */
  tooltipFormatter?: (geoId: string, value: number | null) => string;
  /**
   * Render prop for the tooltip. When provided, it replaces the default
   * string-only tooltip with arbitrary React content — useful for secondary
   * metrics, formatted labels, or rich layouts without requiring the string
   * coercion of `tooltipFormatter`.
   *
   * Receives the same `(geoId, value)` arguments as `tooltipFormatter`.
   * When both props are set, `tooltipContent` takes precedence for the
   * rendered tooltip; `tooltipFormatter` is still used for `aria-label`.
   *
   * @example
   * tooltipContent={(geoId, value) => (
   *   <><strong>{geoId}</strong>: {value?.toLocaleString() ?? 'No data'} enrolled</>
   * )}
   */
  tooltipContent?: (geoId: string, value: number | null) => React.ReactNode;
  /** Show color legend (default `true`) */
  legendVisible?: boolean;
  /** Legend placement (default `'right'`) */
  legendPosition?: 'right' | 'bottom';
  /**
   * SVG stroke width for region borders (default `1`).
   * At state level `1` reads cleanly; at county level you may want `0.5` or
   * less to avoid the border lines dominating small county shapes, especially
   * at lower zoom levels.
   *
   * @example strokeWidth={0.5} // thinner borders for county maps
   */
  strokeWidth?: number;
  /**
   * Multiplier applied to the auto-computed projection scale (default `1.2`).
   * Values > 1 zoom in, values < 1 zoom out. Applies to both `us-states` and
   * `counties` levels. Ignored when `projectionConfig.scale` is set explicitly.
   *
   * @example scaleFactor={1.5} // zoom in 50%
   * @example scaleFactor={0.8} // zoom out 20%
   */
  scaleFactor?: number;
  /**
   * Enable interactive zoom + pan controls (default `true`).
   * When enabled, users can scroll-wheel zoom, click-drag pan, and use the
   * floating +/− buttons. Disable for static embed contexts.
   */
  zoomable?: boolean;
  /** Container height in px (default `400`) */
  height?: number;
  /** Fill parent container height */
  fillContainer?: boolean;
}

interface TooltipState {
  geoId: string;
  name: string;
  value: number | null;
  x: number;
  y: number;
}

/**
 * HeatmapChart — choropleth composite using direct d3-geo SVG rendering.
 * Intentionally does NOT use ChartContainer (a Recharts/shadcn primitive) —
 * that injects Recharts CSS color variables which are not applicable here.
 */
export const HeatmapChart = React.forwardRef<HTMLDivElement, HeatmapChartProps>(
  (
    {
      data,
      level,
      stateFips: stateFipsProp,
      projectionConfig: projectionConfigProp,
      scaleFactor = 1.2,
      zoomable = true,
      normalization = 'linear',
      colorScale = 'sequential',
      colorScaleConfig,
      tooltipFormatter: tooltipFormatterProp,
      tooltipContent,
      legendVisible = true,
      legendPosition = 'right',
      strokeWidth = 1,
      height = 400,
      fillContainer = false,
    },
    ref,
  ) => {
    const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);
    const [focusedGeoId, setFocusedGeoId] = React.useState<string | null>(null);

    // Internal ref always attached to the root div so we can resolve
    // ownerDocument for the portal target. This makes tooltips render inside
    // the same document as the map — critical in iframe contexts (e.g. the
    // Puck admin preview) where document.body would be the parent frame's body
    // and mouse coordinates would be in the wrong coordinate space.
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    // Build geoId → value map for O(1) lookups.
    const geoIdToValue = React.useMemo(() => {
      const map = new Map<string, number | null>();
      for (const row of data) {
        map.set(row.geoId, row.value);
      }
      return map;
    }, [data]);

    const isCountyLevel = level === 'counties';
    // Treat empty/whitespace stateFips as absent.
    const stateFips = stateFipsProp?.trim() || undefined;

    // Pre-validate stateFips so the warning can be emitted from a useEffect
    // (not from inside useMemo), keeping geoFeatures a pure computation.
    // "Valid" means absent (handled by missing-state-fips warning separately)
    // or a 1–2 digit numeric string.
    const isStateFipsValid = !stateFips || /^\d{1,2}$/.test(stateFips);

    // Warn once (module-level dedup) when county level is used without stateFips.
    React.useEffect(() => {
      if (!isCountyLevel || stateFips) return;
      warnOnce(
        'HeatmapChart/missing-state-fips',
        '[hen-ui/HeatmapChart] `level="counties"` requires a `stateFips` prop (2-digit state FIPS code). ' +
          'No fallback is applied — the map will not render until `stateFips` is provided.',
      );
    }, [isCountyLevel, stateFips]);

    // Warn once when stateFips is present but fails format validation.
    React.useEffect(() => {
      if (isCountyLevel && stateFips && !isStateFipsValid) {
        warnOnce(
          `HeatmapChart/invalid-state-fips:${stateFips}`,
          `[hen-ui/HeatmapChart] Invalid stateFips "${stateFips}" provided. Expected 1 or 2 digit FIPS code. No counties will be rendered.`,
        );
      }
    }, [isCountyLevel, stateFips, isStateFipsValid]);
    // Convert TopoJSON → GeoJSON features once per level/stateFips change.
    const geoFeatures = React.useMemo(() => {
      if (!isCountyLevel) {
        const topo = usStatesTopo as unknown as Topology;
        const objectKey = Object.keys(topo.objects)[0];
        const collection = feature(
          topo,
          topo.objects[objectKey] as GeometryCollection,
        );
        return collection.features;
      }
      // Guard: stateFips is required for county level.
      if (!stateFips) return [];
      // Validation result is pre-computed outside the memo to avoid side
      // effects during render. Return empty here; useEffect emits the warning.
      if (!isStateFipsValid) return [];
      const paddedStateFips = stateFips.padStart(2, '0');
      // Filter to features whose 5-digit FIPS starts with the 2-digit state prefix.
      const topo = usCountiesTopo as unknown as Topology;
      const objectKey = Object.keys(topo.objects)[0];
      const collection = feature(
        topo,
        topo.objects[objectKey] as GeometryCollection,
      );
      return collection.features.filter((f) =>
        String(f.id).startsWith(paddedStateFips),
      );
    }, [isCountyLevel, stateFips, isStateFipsValid]);

    // Build geoId → human-readable name map from TopoJSON feature properties.
    // us-atlas features carry `properties.name` (e.g. "North Carolina" for states,
    // "Wake" for counties). Falls back to the raw geoId string when absent.
    const geoIdToName = React.useMemo(
      () =>
        new Map<string, string>(
          geoFeatures.map((f) => [
            String(f.id ?? ''),
            String(
              (f.properties as { name?: string } | null)?.name ?? f.id ?? '',
            ),
          ]),
        ),
      [geoFeatures],
    );

    // Build the d3-geo projection. For counties we use Mercator and auto-fit
    // the bounding box to the feature set; for US states we use AlbersUsa.
    // Operator `projectionConfigProp` overrides the auto-computed values.
    const projection = React.useMemo(() => {
      if (isCountyLevel) {
        const proj = geoMercator().translate([400, 250]);
        if (geoFeatures.length > 0) {
          proj.fitSize([800, 500], {
            type: 'FeatureCollection',
            features: geoFeatures,
          });
          // Apply scaleFactor while keeping the viewport center (400, 250)
          // fixed. Changing scale alone shifts the features by thousands of
          // pixels (US counties are far from the Mercator origin), so we must
          // re-derive translate to compensate.
          if (!projectionConfigProp?.scale) {
            const s0 = proj.scale();
            const [tx0, ty0] = proj.translate();
            const s1 = s0 * scaleFactor;
            const ratio = s1 / s0;
            proj
              .scale(s1)
              .translate([
                400 - ratio * (400 - tx0),
                250 - ratio * (250 - ty0),
              ]);
          }
        }
        if (projectionConfigProp?.center)
          proj.center(projectionConfigProp.center);
        if (projectionConfigProp?.scale) proj.scale(projectionConfigProp.scale);
        return proj;
      }
      // geoAlbersUsa base scale is 1070; scaleFactor shifts the zoom uniformly.
      return geoAlbersUsa()
        .scale(projectionConfigProp?.scale ?? 1070 * scaleFactor)
        .translate([400, 250]);
    }, [isCountyLevel, geoFeatures, scaleFactor, projectionConfigProp]);

    const pathGenerator = React.useMemo(
      () => geoPath().projection(projection),
      [projection],
    );

    // Calculate nonNullValues early (before any early returns) for legend use.
    const nonNullValues = React.useMemo(
      () => data.map((d) => d.value).filter((v): v is number => v !== null),
      [data],
    );
    // Use reduce instead of spread to avoid stack overflow on very large datasets.
    const observedMin =
      nonNullValues.length > 0
        ? nonNullValues.reduce((a, b) => (b < a ? b : a))
        : 0;
    const observedMax =
      nonNullValues.length > 0
        ? nonNullValues.reduce((a, b) => (b > a ? b : a))
        : 1;
    const domainMin =
      colorScaleConfig?.domain?.[0] ??
      // For sequential scales with no custom domain, anchor at 0 so the
      // gradient starts at "none / zero" and the lightest blue always means
      // "very low but present" rather than just "the dataset minimum".
      // Diverging scales keep the observed min so the midpoint stays correct.
      (colorScale === 'sequential' ? Math.min(0, observedMin) : observedMin);
    const domainMax = colorScaleConfig?.domain?.[1] ?? observedMax;

    // Warn for unknown geoIds (once per instance lifecycle, not per render).
    // Reset the warned set when level changes so a new level gets fresh warnings.
    const warnedGeoIds = React.useRef<Set<string>>(new Set());
    React.useEffect(() => {
      warnedGeoIds.current = new Set();
    }, [level]);
    React.useEffect(() => {
      if (data.length === 0) return;
      const featureIdSet = new Set(geoFeatures.map((f) => String(f.id)));
      for (const row of data) {
        if (
          !featureIdSet.has(row.geoId) &&
          !warnedGeoIds.current.has(row.geoId)
        ) {
          warnedGeoIds.current.add(row.geoId);
          warnOnce(
            `HeatmapChart/unknown-geo-id:${row.geoId}:${level}`,
            `[hen-ui/HeatmapChart] geoId "${row.geoId}" did not match any feature in level="${level}" — row skipped.`,
          );
        }
      }
    }, [data, geoFeatures, level]);

    // Warn once per empty-data occurrence outside of useMemo to avoid side
    // effects in a pure computation context and double-firing in StrictMode.
    React.useEffect(() => {
      if (data.length === 0) {
        warnOnce(
          'HeatmapChart/empty-data',
          '[hen-ui/HeatmapChart] data is empty — all regions rendered in no-data color',
        );
      }
    }, [data]);

    // Warn (once per misconfiguration) when quantile + diverging are combined —
    // this is an unsupported pairing that produces a misleading gradient legend.
    // Kept in useEffect rather than useMemo to avoid side effects during render.
    React.useEffect(() => {
      if (normalization === 'quantile' && colorScale === 'diverging') {
        warnOnce(
          'HeatmapChart/quantile-diverging',
          '[hen-ui/HeatmapChart] `normalization="quantile"` with `colorScale="diverging"` is not supported. ' +
            'The legend will show a gradient instead of discrete buckets. Use `normalization="linear"` with `colorScale="diverging"`.',
        );
      }
    }, [normalization, colorScale]);

    // Build d3 color scale in useMemo.
    // Reuses the memoized nonNullValues / domainMin / domainMax computed above
    // to avoid a second pass over data and duplicate Math.min/max spreads.
    const colorScaleFn = React.useMemo(() => {
      if (nonNullValues.length === 0) {
        return () => NO_DATA_COLOR;
      }

      const range = colorScaleConfig?.range ?? SEQUENTIAL_DEFAULT_RANGE;

      if (colorScale === 'diverging') {
        // 3-stop diverging: range[0] → neutral → range[1]
        const divRange = colorScaleConfig?.range
          ? [
              colorScaleConfig.range[0],
              DIVERGING_DEFAULT_COLORS[1],
              colorScaleConfig.range[1],
            ]
          : DIVERGING_DEFAULT_COLORS;
        const mid = (domainMin + domainMax) / 2;
        const scale = scaleLinear<string>()
          .domain([domainMin, mid, domainMax])
          .range(divRange)
          .clamp(true);
        return (v: number) => scale(v);
      }

      if (normalization === 'quantile') {
        // 5 equally-interpolated buckets between range[0] and range[1].
        const buckets = 5;
        const bucketColors: string[] = [];
        const linearInterp = scaleLinear<string>()
          .domain([0, buckets - 1])
          .range(range);
        for (let i = 0; i < buckets; i++) {
          bucketColors.push(linearInterp(i));
        }
        const scale = scaleQuantile<string>()
          .domain(nonNullValues)
          .range(bucketColors);
        return (v: number) => scale(v);
      }

      if (normalization === 'log') {
        // log1p mapping: shift all values by +1-abs(min) when min ≤ 0 to keep domain positive.
        const shift = domainMin <= 0 ? Math.abs(domainMin) + 1 : 0;
        const safeDomainMin = Math.max(domainMin + shift, Number.EPSILON);
        const safeDomainMax = domainMax + shift;
        const scale = scaleLog<string>()
          .domain([safeDomainMin, safeDomainMax])
          .range(range)
          .clamp(true);
        return (v: number) => scale(Math.max(v + shift, Number.EPSILON));
      }

      // linear (default)
      const scale = scaleLinear<string>()
        .domain([domainMin, domainMax])
        .range(range)
        .clamp(true);
      return (v: number) => scale(v);
    }, [
      nonNullValues,
      domainMin,
      domainMax,
      normalization,
      colorScale,
      colorScaleConfig,
    ]);

    const handleMouseEnter = React.useCallback(
      (featureId: string | number | undefined, e: MouseEvent) => {
        const geoId = String(featureId ?? '');
        const value = geoIdToValue.get(geoId) ?? null;
        const name = geoIdToName.get(geoId) ?? geoId;
        setTooltip({ geoId, name, value, x: e.clientX, y: e.clientY });
      },
      [geoIdToValue, geoIdToName],
    );

    const handleMouseLeave = React.useCallback(() => {
      setTooltip(null);
    }, []);

    // Container-level keyboard focus handler (mirrors the pointer event pattern).
    // Uses focusin/focusout (both bubble) rather than React's onFocus/onBlur
    // because Geography from react-simple-maps may not forward arbitrary props,
    // and React 18 itself delegates focus via the bubbling focusin event.
    const handleGeoFocus = React.useCallback(
      (e: FocusEvent) => {
        let el: Element | null = e.target as Element;
        while (el) {
          const geoId = (el as HTMLElement).dataset?.geoId;
          if (geoId !== undefined) {
            const value = geoIdToValue.get(geoId) ?? null;
            const name = geoIdToName.get(geoId) ?? geoId;
            // Use the bounding rect center as tooltip anchor — gives a stable
            // position above the region regardless of the pointer location.
            const rect = el.getBoundingClientRect();
            setFocusedGeoId(geoId);
            setTooltip({
              geoId,
              name,
              value,
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            });
            return;
          }
          el = el.parentElement;
        }
      },
      [geoIdToValue, geoIdToName],
    );

    const handleGeoBlur = React.useCallback((e: FocusEvent) => {
      // Only clear if focus is leaving the geo map entirely — if focus is
      // moving to another geo region, the subsequent focusin will take over.
      const next = e.relatedTarget as Element | null;
      if (next) {
        let el: Element | null = next;
        while (el) {
          if ((el as HTMLElement).dataset?.geoId !== undefined) return;
          el = el.parentElement;
        }
      }
      setFocusedGeoId(null);
      setTooltip(null);
    }, []);

    // Single container-level mousemove listener — the same architecture
    // Recharts uses. Avoids binding onMouseEnter/onMouseLeave to every
    // individual <Geography> path, which is blocked by registerOverlayPortal
    // in iframe-embedded contexts (e.g. Puck admin preview).
    //
    // On each mousemove we walk up from e.target to find a path element that
    // carries data-geo-id. If found, we update the tooltip; if the cursor
    // moves onto a non-geo area (background, zoom buttons, etc.) we clear it.
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // RAF state — local to this effect closure so they're cleaned up automatically.
      let rafId: number | null = null;
      let pendingMoveEvent: PointerEvent | null = null;

      // jsdom's requestAnimationFrame never fires during synchronous test
      // assertions, so we detect it and process events immediately instead.
      // In real browsers the RAF path throttles updates to ~60 fps.
      const isJsdom =
        typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);

      const processMove = (pending: PointerEvent) => {
        // Ignore movements over the zoom-control buttons — they sit inside the
        // container but are not geo paths, so we should not clear the tooltip
        // when the cursor moves over them. Using .closest() avoids a repeated
        // querySelector scan on every mousemove event.
        if (
          (pending.target as Element | null)?.closest?.('[data-zoom-controls]')
        )
          return;

        let el: Element | null = pending.target as Element;
        while (el && el !== container) {
          const geoId = (el as HTMLElement).dataset?.geoId;
          if (geoId !== undefined) {
            handleMouseEnter(geoId, pending);
            return;
          }
          el = el.parentElement;
        }
        // Cursor is over a non-geo area inside the container — clear tooltip.
        handleMouseLeave();
      };

      const onMove = (e: PointerEvent) => {
        if (isJsdom) {
          // Skip RAF in jsdom — process synchronously so tests can assert
          // tooltip state immediately after fireEvent.pointerMove/pointerDown.
          processMove(e);
          return;
        }
        // Throttle to one update per animation frame in real browsers.
        pendingMoveEvent = e;
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          const pending = pendingMoveEvent;
          if (!pending) return;
          pendingMoveEvent = null;
          processMove(pending);
        });
      };

      // On touch devices, pointermove only fires when the finger drags — a
      // static tap never triggers it. pointerdown fires on every tap (and on
      // mouse-click), so we use it to show/update the tooltip immediately.
      // For mouse users this is harmless — the tooltip appears on click and
      // then tracks on subsequent pointermove.
      const onTap = (e: PointerEvent) => processMove(e);

      const onLeave = () => handleMouseLeave();

      // pointermove  — hover on desktop, drag-preview on touch
      // pointerdown  — tap-to-show on touch (pointerType === 'touch' guard)
      // pointerleave — dismiss when pointer exits the container
      // focusin/out  — keyboard navigation: show tooltip on geo focus, hide on blur
      container.addEventListener('pointermove', onMove);
      container.addEventListener('pointerdown', onTap);
      container.addEventListener('pointerleave', onLeave);
      container.addEventListener('focusin', handleGeoFocus);
      container.addEventListener('focusout', handleGeoBlur);
      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        container.removeEventListener('pointermove', onMove);
        container.removeEventListener('pointerdown', onTap);
        container.removeEventListener('pointerleave', onLeave);
        container.removeEventListener('focusin', handleGeoFocus);
        container.removeEventListener('focusout', handleGeoBlur);
      };
    }, [handleMouseEnter, handleMouseLeave, handleGeoFocus, handleGeoBlur]);

    // ─── All hooks must be declared above this line ───────────────────────────
    // Misconfiguration guard: county level requires stateFips.
    // This early return is safe because every hook in this component is
    // declared unconditionally before this point.
    if (isCountyLevel && !stateFips) {
      return (
        <div
          role='alert'
          className='flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4'
          style={fillContainer ? { minHeight: height } : { height }}
        >
          Set <code className='mx-1 font-mono'>stateFips</code> to render a
          county map.
        </div>
      );
    }

    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    // Determine effective range for legend rendering.
    const effectiveRange: [string, string] =
      colorScale === 'diverging' && !colorScaleConfig?.range
        ? [DIVERGING_DEFAULT_COLORS[0], DIVERGING_DEFAULT_COLORS[2]]
        : (colorScaleConfig?.range ?? SEQUENTIAL_DEFAULT_RANGE);

    const mapContent = (
      <svg
        viewBox='0 0 800 500'
        preserveAspectRatio='xMidYMid meet'
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        {geoFeatures.map((geo) => {
          const geoId = String(geo.id ?? '');
          // Single lookup — derive both fill and aria-label from the same value.
          const value = geoIdToValue.get(geoId) ?? null;
          const name = geoIdToName.get(geoId) ?? geoId;
          const fill = value == null ? NO_DATA_COLOR : colorScaleFn(value);
          const pathD = pathGenerator(geo as GeoPermissibleObjects);
          // geoAlbersUsa returns null for coordinates outside the US extent.
          if (!pathD) return null;
          const valueLabel = value !== null ? String(value) : 'No data';
          const ariaLabel = tooltipFormatterProp
            ? tooltipFormatterProp(geoId, value)
            : `${name}: ${valueLabel}`;
          const isFocused = geoId === focusedGeoId;
          return (
            <path
              key={geoId}
              d={pathD}
              fill={fill}
              // Focused regions use a thicker contrast stroke to satisfy
              // WCAG 2.4.7 (Focus Visible) and 2.4.11 (Focus Appearance).
              // SVG paths don't support CSS outline, so stroke is the
              // correct substitute for a focus ring on an SVG element.
              stroke={isFocused ? '#1d4ed8' : '#cbd5e1'}
              strokeWidth={isFocused ? 2 : strokeWidth}
              tabIndex={0}
              aria-label={ariaLabel}
              data-geo-id={geoId}
              data-testid={`geo-${geoId}`}
              className='cursor-pointer outline-none hover:opacity-80'
            />
          );
        })}
      </svg>
    );

    const mapElement = (
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {zoomable ? (
          <TransformWrapper
            minScale={0.5}
            maxScale={8}
            limitToBounds={false}
            doubleClick={{ disabled: true }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {/* Floating zoom controls — data-zoom-controls prevents the
                    container mousemove handler from clearing the tooltip when
                    the cursor moves over these buttons. */}
                <div
                  data-zoom-controls
                  className='absolute right-2 top-2 z-10 flex flex-col gap-0.5'
                >
                  {(
                    [
                      { label: '+', title: 'Zoom in', onClick: () => zoomIn() },
                      {
                        label: '⌖',
                        title: 'Reset zoom',
                        onClick: () => resetTransform(),
                      },
                      {
                        label: '−',
                        title: 'Zoom out',
                        onClick: () => zoomOut(),
                      },
                    ] as const
                  ).map(({ label, title, onClick }) => (
                    <button
                      key={title}
                      title={title}
                      aria-label={title}
                      onClick={onClick}
                      className='flex size-7 cursor-pointer items-center justify-center rounded-xs border border-neutral-100 bg-neutral-white text-sm leading-none text-neutral-700 shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-1'
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <TransformComponent
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  contentStyle={{ width: '100%', height: '100%' }}
                >
                  {mapContent}
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        ) : (
          mapContent
        )}

        {/* Tooltip — portaled into the same document as the map so that
            position: fixed coordinates are relative to the correct viewport.
            Using ownerDocument.body (rather than the top-level document.body)
            keeps the tooltip in the same frame as the map, which is essential
            in iframe-embedded contexts (e.g. Puck admin preview) where
            clientX/clientY are iframe-local and document.body belongs to the
            parent frame. Falls back to document.body when the ref is not yet
            attached.
            Inline styles are intentional here: this element is portaled outside
            the component tree and Tailwind utility classes are not available in
            the portal target document (e.g. in an iframe without Tailwind CSS). */}
        {tooltip !== null
          ? createPortal(
              <div
                role='tooltip'
                style={{
                  position: 'fixed',
                  left: tooltip.x + 12,
                  top: tooltip.y - 28,
                  // CSS custom properties resolve in the browser even on portaled
                  // elements — safe to use here. Tailwind classes are NOT used
                  // because this element is portaled outside the component tree
                  // (e.g. in an iframe without Tailwind CSS loaded).
                  background: 'var(--neutral-900, #1C1E21)',
                  color: 'var(--neutral-white, #FFFFFF)',
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-xs, 4px)',
                  fontSize: 12,
                  pointerEvents: 'none',
                  zIndex: 9999,
                  // Only enforce nowrap for plain string tooltips; rich ReactNode
                  // layouts (tooltipContent) may intentionally be multi-line.
                  ...(tooltipContent ? {} : { whiteSpace: 'nowrap' }),
                }}
              >
                {tooltipContent
                  ? tooltipContent(tooltip.geoId, tooltip.value)
                  : tooltipFormatterProp
                    ? tooltipFormatterProp(tooltip.geoId, tooltip.value)
                    : `${tooltip.name}: ${tooltip.value !== null ? String(tooltip.value) : 'No data'}`}
              </div>,
              containerRef.current?.ownerDocument?.body ?? document.body,
            )
          : null}
      </div>
    );

    const legendElement = legendVisible ? (
      <Legend
        colorScale={colorScale}
        normalization={normalization}
        range={effectiveRange}
        domainMin={domainMin}
        domainMax={domainMax}
        nonNullValues={nonNullValues}
        position={legendPosition ?? 'right'}
      />
    ) : null;

    return (
      <div
        ref={mergedRef}
        role='figure'
        aria-label='Geographic heatmap'
        className={sizingClass}
        style={{
          ...sizingStyle,
          display: 'flex',
          flexDirection: legendPosition === 'bottom' ? 'column' : 'row',
          gap: 8,
        }}
      >
        {mapElement}
        {legendElement}
      </div>
    );
  },
);
HeatmapChart.displayName = 'HeatmapChart';

// ────────────────────────────────────────────────────────────────────────────
// Legend sub-component (rendered outside SVG as a plain div)
// ────────────────────────────────────────────────────────────────────────────

interface LegendProps {
  colorScale: 'sequential' | 'diverging';
  normalization: 'linear' | 'log' | 'quantile';
  range: [string, string];
  domainMin: number;
  domainMax: number;
  nonNullValues: number[];
  position: 'right' | 'bottom';
}

function Legend({
  colorScale,
  normalization,
  range,
  domainMin,
  domainMax,
  nonNullValues,
  position,
}: LegendProps) {
  const isHorizontal = position === 'bottom';

  if (normalization === 'quantile' && colorScale !== 'diverging') {
    const buckets = 5;
    const linearInterp = scaleLinear<string>()
      .domain([0, buckets - 1])
      .range(range);
    const bucketColors: string[] = [];
    for (let i = 0; i < buckets; i++) {
      bucketColors.push(linearInterp(i));
    }

    const scale = scaleQuantile<string>()
      .domain(nonNullValues)
      .range(bucketColors);
    const quantiles = scale.quantiles();

    const labels: string[] = [];
    labels.push(`≤ ${formatLegendValue(quantiles[0] ?? domainMin)}`);
    for (let i = 0; i < quantiles.length - 1; i++) {
      labels.push(
        `${formatLegendValue(quantiles[i])} – ${formatLegendValue(quantiles[i + 1])}`,
      );
    }
    labels.push(
      `> ${formatLegendValue(quantiles[quantiles.length - 1] ?? domainMax)}`,
    );

    // Generate entries in display order directly: dark→light top-to-bottom for
    // vertical legends, light→dark left-to-right for horizontal. No post-hoc
    // reversal needed — the correct index is computed up front.
    const displayEntries = Array.from({ length: buckets }, (_, i) => {
      const idx = isHorizontal ? i : buckets - 1 - i;
      return { color: bucketColors[idx], label: labels[idx] };
    });

    return (
      <div
        role='img'
        aria-label='Color scale legend'
        className={cn(
          'flex flex-col gap-1',
          isHorizontal
            ? 'items-center pt-2'
            : 'min-w-14 max-w-20 justify-center pl-2',
        )}
      >
        <div className='mb-0.5 text-xs text-neutral-500'>Legend</div>
        {displayEntries.map(({ color, label }, i) => (
          <div key={i} className='flex items-center gap-1'>
            <div
              className='size-3.5 shrink-0 border border-neutral-100'
              style={{ backgroundColor: color }}
            />
            <span className='whitespace-nowrap text-xs text-neutral-700'>
              {label}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Sequential gradient or diverging
  const gradientColors =
    colorScale === 'diverging'
      ? [range[0], DIVERGING_DEFAULT_COLORS[1], range[1]]
      : [range[0], range[1]];
  const gradientCss = `linear-gradient(${isHorizontal ? 'to right' : 'to top'}, ${gradientColors.join(', ')})`;

  return (
    <div
      role='img'
      aria-label='Color scale legend'
      className={cn(
        'flex flex-col gap-1',
        isHorizontal
          ? 'items-center pt-2'
          : 'min-w-14 max-w-20 justify-center pl-2',
      )}
    >
      <div className='mb-0.5 text-xs text-neutral-500'>Legend</div>
      {isHorizontal ? (
        <div className='flex flex-col items-center gap-0.5'>
          <div
            className='h-3 w-40 border border-neutral-100'
            style={{ background: gradientCss }}
          />
          <div className='flex w-40 justify-between text-xs text-neutral-700'>
            <span>{formatLegendValue(domainMin)}</span>
            <span>{formatLegendValue(domainMax)}</span>
          </div>
        </div>
      ) : (
        <div className='flex flex-row items-stretch gap-1'>
          <div
            className='h-20 w-3.5 border border-neutral-100'
            style={{ background: gradientCss }}
          />
          <div className='flex flex-col justify-between text-xs text-neutral-700'>
            <span>{formatLegendValue(domainMax)}</span>
            <span>{formatLegendValue(domainMin)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatLegendValue(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}
